import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientIframePackageDataModel, ClientIframeProductModel, ClientPackageModel, IframeCategoryItem, ISettingsMenu, OptionsIframeModel, ProductModel } from '@atlas-workspace/shared/models';
import { environment } from '@environment-client';
import { plainToClass } from 'class-transformer';
import { BehaviorSubject, map, Observable, share, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OptionsIframeService {
  private _categoriesMenu$ = new BehaviorSubject<ISettingsMenu[]>([]);
  private _categories$ = new BehaviorSubject<IframeCategoryItem[]>([]);
  private _totalPrice$ = new BehaviorSubject<number>(0);
  private _showPrices$ = new BehaviorSubject<boolean>(true);
  private _selectionToken$ = new BehaviorSubject<string>('');
  private _introduction$ = new BehaviorSubject<IframeCategoryItem | null>(null);
  private _packages$ = new BehaviorSubject<ClientIframePackageDataModel | null>(null);
  private _selectedPackage$ = new BehaviorSubject<ClientPackageModel | null>(null);

  public locale = 'no';
  private readonly apiUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) { }

  private getHttpHeaders(): HttpHeaders {
    return new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Accept-Language': this.locale,
    });
  }

  public setSelectedPackage(value: ClientPackageModel | null): void {
    this._selectedPackage$.next(value);
  }

  get selectedPackage$(): Observable<ClientPackageModel | null> {
    return this._selectedPackage$.asObservable();
  }

  public setCategories(data: IframeCategoryItem[], projectId: number, tokenId: string, skipNavUpdate = false): void {
    const menu = data.map((x) => ({
      id: x.id,
      name: x.name,
      disabled: false,
      route:
        `/options/project/${projectId}/token/${tokenId}/category/${x.id}` + (x.is_introduction ? '/introduction' : ''),
    }));
    if (!skipNavUpdate) {
      this._categoriesMenu$.next(menu);
    }
  }

  public setNavMenu(menu: ISettingsMenu[]): void {
    this._categoriesMenu$.next(menu);
  }

  public get packages$(): Observable<ClientIframePackageDataModel | null> {
    return this._packages$.asObservable();
  }

  public setPackages(packages: ClientIframePackageDataModel): void {
    this._packages$.next(packages);
  }


  get categoriesData$(): Observable<IframeCategoryItem[]> {
    return this._categories$.asObservable().pipe(share());
  }

  get introduction$(): Observable<IframeCategoryItem | null> {
    return this._introduction$.asObservable();
  }

  public setIntroduction(value: IframeCategoryItem): void {
    this._introduction$.next(value);
  }

  public updateCategories(data: IframeCategoryItem[]): void {
    this._categories$.next(data);
  }

  /**
    * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Packages/paths/~1api~1v1~1iframes~1%7Biframe_token%7D~1packages~1%7Bid%7D~1unselect/post
    */
  public selectPackage(iframeToken: string, id: number, selectionToken?: string): Observable<string> {
    let params = new HttpParams();
    if (selectionToken) {
      params = params.set('selection_token', selectionToken);
    }
    return this.http
      .post(`${this.apiUrl}api/v1/iframes/${iframeToken}/packages/${id}/select`, null, 
        {
          headers: this.getHttpHeaders(),
          params: params,
        }
      ).pipe(
        map((res: any) =>  res.selection_token ),
      ) 
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Packages/paths/~1api~1v1~1iframes~1%7Biframe_token%7D~1packages~1%7Bid%7D~1unselect/post
   */
  public unselectPackage(iframeToken: string, id: number, selectionToken?: string): Observable<any> {
    let params = new HttpParams();
    if (selectionToken) {
      params = params.set('selection_token', selectionToken);
    }
  
    return this.http.post(
      `${this.apiUrl}api/v1/iframes/${iframeToken}/packages/${id}/unselect`,
      null,
      {
        headers: this.getHttpHeaders(),
        params: params,
      }
    );
  }

  public get showPrices$(): Observable<boolean> {
    return this._showPrices$.asObservable();
  }

  public get selectionToken$(): Observable<string> {
    return this._selectionToken$.asObservable().pipe(share());
  }

  public setSelectionToken(value: string): void {
    this._selectionToken$.next(value);
  }

  get optionCategories$(): Observable<ISettingsMenu[]> {
    return this._categoriesMenu$.asObservable();
  }

  public setTotalPrice(value: number): void {
    this._totalPrice$.next(value);
  }

  public get totalPrice$(): Observable<number> {
    return this._totalPrice$.asObservable();
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Layout-Option-Categories
   */
  public getIframeCategories(token: string, selectionToken?: string): Observable<IframeCategoryItem[]> {
    let params = new HttpParams();
    if (selectionToken) {
      params = params.set('selection_token', selectionToken);
    }
    return this.http
      .get(`${this.apiUrl}api/v1/iframes/${token}/categories`, {
        headers: this.getHttpHeaders(),
        params: params,
      })
      .pipe(
        map((res: any) => {
          const showPrices = res.data.show_prices;
          this._showPrices$.next(showPrices);
          return res.data.categories;
        }),
      );
  }

    /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Products/paths/~1api~1v1~1iframes~1{iframe_token}~1products~1selected/get
   */
  getIframeSelectedItems(iframeToken: string, selectionToken: string): Observable<ClientIframeProductModel[]> {
    let params = new HttpParams();
    if (selectionToken) {
      params = params.set('selection_token', selectionToken);
    }
    return this.http
      .get(`${this.apiUrl}api/v1/iframes/${iframeToken}/products/selected`, {
        headers: this.getHttpHeaders(),
        params: params,
      })
      .pipe(
        map((res: any) => res.data.products),
        map((data: any[]) => {
          return plainToClass(ClientIframeProductModel, data)
        })
      );
  }

  /**
  * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Packages/paths/~1api~1v1~1iframes~1%7Biframe_token%7D~1packages/get
  */
  public getIframePackages(token: string, selectionToken?: string): Observable<ClientIframePackageDataModel> {
    let params = new HttpParams();
    if (selectionToken) {
      params = params.set('selection_token', selectionToken);
    }
    return this.http
      .get(`${this.apiUrl}api/v1/iframes/${token}/packages`, 
        {
          headers: this.getHttpHeaders(),
          params: params,
        }
      )
      .pipe(
        map((res: any) => res.data),
        map((packageData) => {
          return plainToClass(ClientIframePackageDataModel, packageData)
        }
        ),
        tap((packages) => {
          this.setPackages(packages);
        }),
      );
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Products
   */
  public getIframeProduct(token: string, productId: number, selectionToken?: string): Observable<ProductModel> {
    let params = new HttpParams();
    if (selectionToken) {
      params = params.set('selection_token', selectionToken);
    }
    return this.http
      .get(`${this.apiUrl}api/v1/iframes/${token}/products/${productId}`, {
        headers: this.getHttpHeaders(),
        params: params,
      })
      .pipe(
        map((res: any) => res.data),
        map((product) => plainToClass(ProductModel, product)),
      );
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Layout-Option-Categories/paths/~1api~1v1~1iframes~1%7Biframe_token%7D~1categories/get
   */
  public getCategoryDetails(
    token: string,
    categoryId: number,
    selectionToken?: string,
  ): Observable<OptionsIframeModel> {
    let params = new HttpParams();

    if (selectionToken) {
      params = params.set('selection_token', selectionToken);
    }

    return this.http
      .get(`${this.apiUrl}api/v1/iframes/${token}/categories/${categoryId}`, {
        headers: this.getHttpHeaders(),
        params,
      })
      .pipe(
        map((res: any) => res.data),
        map((x) => plainToClass(OptionsIframeModel, x)),
      );
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Iframes-Products/paths/~1api~1v1~1iframes~1{iframe_token}~1products~1batch_select/post
   */
  public saveIframeSelectedItems(
    tokenId: string,
    email: string,
    selectedItems: any,
    selectionToken?: string,
  ): Observable<string> {
    const body = {
      selection_token: selectionToken,
      email: email,
      items: selectedItems,
    };

    return this.http
      .post(`${this.apiUrl}api/v1/iframes/${tokenId}/products/batch_select/`, body, {
        headers: this.getHttpHeaders(),
      })
      .pipe(map((res: any) => res.data.token));
  }
}
